import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../api';
import { CurrencySymbol } from '../components/CurrencySymbol';
import { PinInput } from '../components/PinInput';
import { Eye, EyeOff, Shield, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const SetupPage = () => {
    const [step, setStep] = useState(1);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [pin, setPin] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    
    const { token, updateUser } = useAuth();
    const navigate = useNavigate();

    const handlePasswordSubmit = (e) => {
        e.preventDefault();
        setError('');

        if (newPassword.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        setStep(2);
    };

    const handlePinComplete = async (enteredPin) => {
        if (loading) return; // Prevent multiple submissions
        setPin(enteredPin);
        setLoading(true);
        setError('');

        try {
            await authAPI.setup(token, newPassword, enteredPin);
            updateUser({ requires_setup: false });
            // Small delay to ensure state updates before navigation
            setTimeout(() => {
                navigate('/user', { replace: true });
            }, 100);
        } catch (err) {
            setError(err.response?.data?.detail || 'Setup failed');
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-void-black flex items-center justify-center p-4">
            <div className="w-full max-w-md">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-neon-green/10 border border-neon-green/30 mb-4">
                        <Shield className="w-8 h-8 text-neon-green" />
                    </div>
                    <h1 className="text-2xl font-headings font-bold text-white">Account Setup</h1>
                    <p className="text-zinc-500 mt-1">First time login - secure your account</p>
                </div>

                {/* Progress indicator */}
                <div className="flex items-center justify-center gap-4 mb-8">
                    <div className={`flex items-center gap-2 ${step >= 1 ? 'text-neon-green' : 'text-zinc-600'}`}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-mono ${step >= 1 ? 'bg-neon-green/20 border border-neon-green' : 'bg-zinc-800 border border-zinc-700'}`}>
                            {step > 1 ? <CheckCircle className="w-4 h-4" /> : '1'}
                        </div>
                        <span className="text-sm font-medium">Password</span>
                    </div>
                    <div className={`w-12 h-px ${step >= 2 ? 'bg-neon-green' : 'bg-zinc-700'}`} />
                    <div className={`flex items-center gap-2 ${step >= 2 ? 'text-neon-green' : 'text-zinc-600'}`}>
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-mono ${step >= 2 ? 'bg-neon-green/20 border border-neon-green' : 'bg-zinc-800 border border-zinc-700'}`}>
                            2
                        </div>
                        <span className="text-sm font-medium">PIN</span>
                    </div>
                </div>

                {/* Form container */}
                <div className="glass rounded-lg p-8 animate-slide-up">
                    {error && (
                        <div className="flex items-center gap-2 p-3 mb-6 bg-red-500/10 border border-red-500/20 rounded-md text-red-400 text-sm">
                            <AlertCircle className="w-4 h-4" />
                            {error}
                        </div>
                    )}

                    {step === 1 && (
                        <form onSubmit={handlePasswordSubmit} className="space-y-6">
                            <div>
                                <h2 className="text-lg font-headings font-semibold text-white mb-1">Set New Password</h2>
                                <p className="text-zinc-500 text-sm">Create a strong password for your account</p>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <Label htmlFor="newPassword" className="form-label">New Password</Label>
                                    <div className="relative">
                                        <Input
                                            id="newPassword"
                                            type={showPassword ? 'text' : 'password'}
                                            value={newPassword}
                                            onChange={(e) => setNewPassword(e.target.value)}
                                            placeholder="Enter new password"
                                            className="bg-zinc-900/50 border-zinc-800 focus:border-neon-green text-white pr-10"
                                            data-testid="setup-new-password"
                                            required
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword(!showPassword)}
                                            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                                        >
                                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>

                                <div>
                                    <Label htmlFor="confirmPassword" className="form-label">Confirm Password</Label>
                                    <Input
                                        id="confirmPassword"
                                        type="password"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        placeholder="Confirm new password"
                                        className="bg-zinc-900/50 border-zinc-800 focus:border-neon-green text-white"
                                        data-testid="setup-confirm-password"
                                        required
                                    />
                                </div>
                            </div>

                            <Button
                                type="submit"
                                className="w-full bg-neon-green hover:bg-neon-green/90 text-void-black font-headings font-semibold uppercase tracking-wider h-12"
                                data-testid="setup-password-submit"
                            >
                                Continue to PIN Setup
                            </Button>
                        </form>
                    )}

                    {step === 2 && (
                        <div className="space-y-6">
                            <div className="text-center">
                                <h2 className="text-lg font-headings font-semibold text-white mb-1">Set Your PIN</h2>
                                <p className="text-zinc-500 text-sm">This 4-digit PIN is required for transfers</p>
                            </div>

                            <div className="py-4">
                                <PinInput 
                                    length={4} 
                                    onComplete={handlePinComplete}
                                    disabled={loading}
                                />
                            </div>

                            {loading && (
                                <div className="flex items-center justify-center gap-2 text-zinc-400">
                                    <span className="spinner" />
                                    <span>Setting up your account...</span>
                                </div>
                            )}

                            <button
                                type="button"
                                onClick={() => setStep(1)}
                                className="w-full text-center text-sm text-zinc-500 hover:text-white"
                            >
                                Back to password
                            </button>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-center gap-2 mt-6 text-zinc-600 text-xs">
                    <CurrencySymbol size="sm" />
                    <span className="font-mono">VIRTUAL GAMES WALLET</span>
                </div>
            </div>
        </div>
    );
};

export default SetupPage;
